import axios from 'axios'
import type {
  IndividualDiscount,
  InsuranceDto,
  InsuranceParamsPagedGrid,
  InsuranceParamsPagedQuery,
  InsurerDiscount
} from 'BrokerTsLib/src/dtos'
import { Product } from 'BrokerTsLib/src/enums'
import type { GenericQueryOptions } from 'BrokerTsLib/src/hooks'
import { useMutation, useQueries, useQuery } from 'react-query'

import { baseUrl } from '.'
import { useApp } from './useApp'

const apiUrl = import.meta.env.VITE_INSURANCEPARAMS_VEHICLE_API
const apiTravelUrl = import.meta.env.VITE_INSURANCEPARAMS_TRAVEL_API
const apiPropertyUrl = import.meta.env.VITE_INSURANCEPARAMS_PROPERTY_API
const apiLiabilityUrl = import.meta.env.VITE_INSURANCEPARAMS_LIABILITY_API

export const useUrl = () => {
  const { product } = useApp()

  let url: string | undefined = ''

  switch (product) {
    case Product.TravelInsurance: url = apiTravelUrl; break
    case Product.PropertyInsurance: url = apiPropertyUrl; break
    case Product.LiabilityInsurance: url = apiLiabilityUrl; break
    default: url = apiUrl; break
  }

  return `${baseUrl}/${url}`
}


export const useGetAll = () => {
  const url = useUrl()

  return useMutation((search: InsuranceParamsPagedQuery) =>
    axios.post<InsuranceParamsPagedGrid>(`${url}/GetList`, search))
}


export const useGetInsuranceParamsDiscounts = (
  paramsId: string,
  options?: GenericQueryOptions<InsurerDiscount[]>,
) => {
  const url = useUrl()
  const { product } = useApp()

  return useQuery({
    queryKey: ['useGetInsuranceParamsDiscounts', paramsId, product],
    queryFn: () => axios.get<InsurerDiscount[]>(`${url}/GetDiscounts`, { params: { paramsId } }),
    ...options,
  })
}
export const useGetInsurersSpecificParams = (
  paramsId: string,
  insurerId: number | null,
  options?: GenericQueryOptions<IndividualDiscount[]>,
) => {
  const url = useUrl()
  const { product } = useApp()

  return useQuery({
    queryKey: ['useGetInsurersSpecificParams', paramsId, insurerId, product],
    queryFn: () => axios.get<IndividualDiscount[]>(`${url}/GetInsurersSpecificParams`, { params: { paramsId, insurerId } }),
    enabled: !!paramsId,
    ...options
  })
}

export const useCloneInsuranceParams = (paramsId?: string, replacement: boolean = false) => {
  const url = useUrl()
  return useMutation({
    mutationFn: () =>
      axios.post<{ insuranceParamsId: number }>(
        `${url}/Clone?paramsId=${paramsId}&replacement=${replacement}`,
      ),
  })
}

export const useGetIpid = (paramsId: string) => {
  const url = useUrl()
  return useMutation({
    mutationFn: () => axios.post<{ ipidLink: string }>(`${url}/GetIpid?paramsId=${paramsId}`),
  })
}

export const useFindContractNumber = (
  contractNumber: string,
  insurerId: number,
  options?: GenericQueryOptions<{ insuranceParamsId?: number }>,
) => {
  const url = useUrl()
  const { product } = useApp()

  return useQuery({
    queryKey: ['useFindContractNumber', contractNumber, insurerId, product],
    queryFn: () => axios.post<{ insuranceParamsId?: number }>(`${url}/FindContractNumber`, undefined, { params: { contractNumber, insurerId } }),
    ...options,
  })
}

export const useGetAvailableBusinessmen = (
  paramsId: string,
  options?: GenericQueryOptions<[{ id: string; fullname: string }]>,
) => {
  const url = useUrl()
  const { product } = useApp()

  return useQuery({
    queryKey: ['useGetAvailableBusinessmen', paramsId, product],
    queryFn: () =>
      axios.post<[{ id: string; fullname: string }]>(`${url}/GetAvailableBusinessmen`, undefined, { params: { paramsId } }),
    ...options,
  })
}

export const useDeleteInsuranceParams = () => {
  const url = useUrl()
  return useMutation((data: { paramIds?: string[] }) =>
    axios.post<{ success: boolean; errors?: number[] }>(`${url}/DeleteInsuranceParams`, data),
  )
}

export const useGetInsurance = (offerId: number, options?: GenericQueryOptions<InsuranceDto>) => {
  const url = useUrl()
  const { product } = useApp()

  return useQuery(['useGetInsurance', offerId, product]
    , () => axios.get<InsuranceDto>(`${url}/GetInsurance?offerId=${offerId}`)
    , options)
}

export const useGetInsurances = (offerIds: number[]) => {
  const url = useUrl()
  const { product } = useApp()

  return useQueries(offerIds.map((id) => {
    return {
      queryKey: ['useGetInsurance', id, product],
      queryFn: () => axios.get<InsuranceDto>(`${url}/GetInsurance?offerId=${id}`),
      //...options // , options?: GenericQueryOptions<InsuranceBase>
    }
  }))
}